/**
=========================================================
* Trust Cargo - "add" form definitions
=========================================================

* Each add form used to be its own page with its own copy of the same
* fetch/validate/submit code. They are described here instead, and AddDialog
* renders them - so a new field is one line, not a new screen.
*
* `optionsFrom` lets a select pull its choices from an endpoint at open time,
* which is what the old "Choose User" dropdown did by hand.
*/

import axios from "axios";
import ensureSupplierForShopCode from "utils/suppliers";
import PAY_TYPES from "utils/payType";

const API = "https://trust-cargo.co/anas_backend/api";

/**
 * The last shop code a customer was actually given - what the order form
 * fills in when you pick them.
 *
 * The server sends `last_shop_code` directly. Older deployments only send
 * `next_shop_code`, and the last one is simply that minus one: HIY06 -> HIY05.
 * A next of 01 means the customer has no orders at all, so the bare code is
 * the right answer - HIY, and whoever is typing adds the number.
 *
 * Written to survive either response so the field never comes up empty while
 * a backend change is waiting to be deployed.
 */
export function lastShopCode(customer) {
  if (!customer) return "";
  if (customer.last_shop_code) return customer.last_shop_code;

  const parts = String(customer.next_shop_code || "").match(/^(.*?)(\d+)$/);
  if (!parts) return customer.code || "";

  const [, prefix, digits] = parts;
  const previous = parseInt(digits, 10) - 1;

  // nothing used yet - fall back to the plain three letters
  if (previous < 1) return customer.code || prefix;

  return prefix + String(previous).padStart(digits.length, "0");
}

const entities = {
  user: {
    title: "Add User",
    endpoint: `${API}/register`,
    // the legacy API reads and writes on different URLs than it creates on
    edit: {
      show: (id) => `${API}/specefic_user/${id}`,
      unwrap: "user",
      submit: `${API}/edit_user`,
      method: "post",
      sendId: true,
    },
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "email", label: "Email", required: true },
      /**
       * Required on edit as well, on purpose. CustomerController::editUser
       * runs Hash::make($request->password) unconditionally, so an edit that
       * sends no password stores a hash of null and locks the person out.
       * Until that is guarded server-side, the password is set deliberately
       * every time rather than destroyed silently.
       */
      {
        name: "password",
        label: "Password (set on every save)",
        type: "password",
        required: true,
      },
      { name: "mobile", label: "Mobile" },
      {
        name: "role_id",
        label: "Role",
        type: "select",
        required: true,
        /**
         * The value is the role's NAME, not the id of its row. Every user in
         * the database carries 'customer', 'admin' or 'employee' there, and
         * the people screen sorts staff from customers by reading it - a
         * user saved with "2" belongs to neither.
         */
        optionsFrom: { url: `${API}/roles`, path: "roles", value: "name", label: "name" },
      },
    ],
  },

  customer: {
    title: "Add Customer",
    endpoint: `${API}/add_customer`,
    /**
     * The account, made here rather than on another screen.
     *
     * A customer row is only half a customer: without a user they cannot log
     * in and nothing can be addressed to them. Making that a separate errand
     * - add the user, come back, find them in a list of 114 - is what this
     * removes. Leave Choose User empty and the email and password on this
     * form become the login, on the customer role.
     *
     * Picking an existing user skips all of it, which is what an office does
     * when the person already has an account for something else.
     */
    async beforeSubmit(values) {
      // the login belongs to the account, never to the customer record, so
      // it is stripped whichever way this goes
      const send = { ...values };
      delete send.email;
      delete send.password;

      if (String(values.user_id ?? "").trim()) return send;

      const email = String(values.email ?? "").trim();

      await axios.post(`${API}/register`, {
        name: values.name,
        // omitted rather than sent empty: email is unique, so a second
        // account carrying "" collides with the first one that did
        ...(email ? { email } : {}),
        password: values.password,
        // the customer's own number is the account's number; there is only
        // ever one of them, and with no email it is what they log in with
        mobile: values.phone_number,
        // the column holds the role's name, and this form only ever makes
        // customers - staff are added from the people screen
        role_id: "customer",
      });

      /**
       * /register answers with a token rather than the row it made, so the
       * new account is found by reading the list back. Matched on the email,
       * which is what was just proved unique by the server accepting it, and
       * taking the highest id if a name ever repeats.
       */
      const res = await fetch(`${API}/users`, { cache: "no-store" });
      const list = (await res.json())?.users || [];
      // matched on whichever identifier the account was given, and taking
      // the highest id, which is the row that was just made
      const wanted = (email || String(values.phone_number ?? "").trim()).toLowerCase();

      const account = list
        .filter((u) =>
          [u.email, u.mobile].some(
            (v) =>
              String(v ?? "")
                .trim()
                .toLowerCase() === wanted
          )
        )
        .sort((a, b) => Number(a.id) - Number(b.id))
        .pop();

      if (!account) {
        throw new Error(
          "The account was created but could not be found again - open Choose User and pick it"
        );
      }

      return { ...send, user_id: account.id };
    },
    edit: {
      show: (id) => `${API}/specefic_customer/${id}`,
      unwrap: "customer",
      submit: `${API}/edit_customer`,
      method: "post",
      sendId: true,
    },
    fields: [
      /**
       * All of these are required by CustomerController::addCustomer. They
       * were optional here, so a customer saved with any of them blank came
       * back as one red banner over six filled-in boxes.
       */
      { name: "name", label: "Name", required: true },
      { name: "code", label: "Code", required: true },
      { name: "phone_number", label: "Phone Number", required: true },
      { name: "address", label: "Address", required: true },
      { name: "vat_name", label: "Vat Name", required: true },
      /**
       * The login, asked for only while no existing user is picked. Both go
       * to /register and neither is stored on the customer record.
       *
       * The email is optional: the account can be reached by the phone
       * number above instead, which is what most customers here have - and
       * four of them have instead of an email entirely. One of the two is
       * needed, and the phone is already required for the customer record,
       * so in practice this box is only demanded if that one is empty.
       */
      {
        name: "email",
        label: "Email (their login - or they use the phone number)",
        requiredWhen: (v) =>
          !String(v.user_id ?? "").trim() && !String(v.phone_number ?? "").trim(),
        requiredMessage: "An email or a phone number - one of them is how they log in",
      },
      {
        name: "password",
        label: "Password",
        type: "password",
        requiredWhen: (v) => !String(v.user_id ?? "").trim(),
      },
      {
        name: "user_id",
        label: "Choose User - only if they already have an account",
        type: "select",
        optionsFrom: { url: `${API}/users`, path: "users", value: "id", label: "name" },
        // somebody who has no account yet is created from this box rather
        // than from another screen and a fresh start on this form
        createEntity: "user",
      },
    ],
  },

  container: {
    title: "Add Container",
    endpoint: `${API}/add_container`,
    edit: {
      show: (id) => `${API}/specefic_container_data/${id}`,
      unwrap: "container",
      // NOTE: the old edit_container page posted to /edit_customer - almost
      // certainly a copy-paste slip. This uses the container endpoint.
      submit: `${API}/edit_container`,
      method: "post",
      sendId: true,
    },
    wide: true,
    // addContainer and editContainer both read all of these; leaving a field
    // out of the form would blank it on the server on the next edit
    fields: [
      { name: "name", label: "Container Name", required: true },
      { name: "number", label: "Container Number" },
      { name: "status", label: "Status (loading / shipped / delivered)" },
      { name: "telex_status", label: "Telex (not_yet / released)" },
      { name: "loading_date", label: "Loading date", type: "date" },
      { name: "estimated_arrival_date", label: "Estimated arrival", type: "date" },
      { name: "notes", label: "Notes", multiline: true },
    ],
  },

  /**
   * The same container form, but through the endpoints that know about
   * types. Used once container_types exists on the server; before that the
   * `container` entity above is used and there is no type to choose.
   */
  container_typed: {
    title: "Add Container",
    endpoint: `${API}/v2/typed-containers`,
    wide: true,
    fields: [
      { name: "name", label: "Container Name", required: true },
      { name: "number", label: "Container Number" },
      {
        name: "container_type_id",
        label: "Type",
        type: "select",
        optionsFrom: { url: `${API}/v2/container-types`, value: "id", label: "name" },
      },
      /**
       * Both of these are ENUM columns. As free-text boxes they took
       * anything and handed the database a value it refused, which came
       * back as a 500 on save; the choices are now read from the column
       * definition itself, so the form cannot offer what will not store.
       */
      {
        name: "status",
        label: "Status",
        type: "select",
        optionsFrom: {
          url: `${API}/v2/container-field-options`,
          path: "status",
          value: "value",
          label: "label",
        },
      },
      /**
       * Telex is not an ENUM on the server, it is a plain text column, so
       * there is no fixed list to read - the office keeps its own, and
       * `manage` says the box may write to it: a new wording is added from
       * the dropdown, an existing one renamed, a wrong one deleted, without
       * leaving this form.
       *
       * `allowNew` stays as well, so a one-off wording can still be typed
       * and saved on this container alone without joining the list.
       */
      {
        name: "telex_status",
        label: "Telex",
        type: "select",
        allowNew: true,
        optionsFrom: {
          url: `${API}/v2/container-field-options`,
          path: "telex_status",
          value: "value",
          label: "label",
        },
        manage: {
          url: `${API}/v2/container-field-options`,
          field: "telex_status",
        },
      },
      /**
       * The container's own timeline, in the order it happens: when it is
       * meant to load, when it actually loaded, when it should land, when it
       * was received, and when the port inspected it.
       */
      { name: "expected_loading_date", label: "Expected loading day", type: "date" },
      { name: "loading_date", label: "Loading date", type: "date" },
      { name: "estimated_arrival_date", label: "Estimated arrival", type: "date" },
      { name: "received_date", label: "Container received", type: "date" },
      { name: "port_inspection_date", label: "Port inspection", type: "date" },
      { name: "notes", label: "Notes", multiline: true },
    ],
  },

  status: {
    title: "Add Status",
    endpoint: `${API}/add_status`,
    edit: {
      show: (id) => `${API}/specefic_status/${id}`,
      unwrap: "status",
      submit: `${API}/edit_status`,
      method: "post",
      sendId: true,
    },
    fields: [{ name: "name", label: "Status Name", required: true }],
  },

  /**
   * One row on the Follow up sheet - and nothing else.
   *
   * Left out on purpose, because the system works them out:
   *   Customer Name  <- the first 3 letters of the shop code
   *   Deposit        <- deposits on the order, from both ledgers: the
   *                     customer's to us and ours to the supplier
   *   payment        <- sum of payments in the customer ledger
   *   Balance        <- final amount minus what was paid
   *   EX AMOUNT      <- mirrors Balance
   * There are no order line-items here either; this creates the order row.
   */
  order: {
    title: "Create Order",
    endpoint: `${API}/v2/orders`,
    wide: true,
    /**
     * A new order leaves a supplier of the same shop code behind. The server
     * does this too, in the same transaction; this covers servers that have
     * not been updated yet and is a no-op once they have.
     */
    afterCreate: (values) => ensureSupplierForShopCode(values.shop_code),
    fields: [
      /**
       * Picking the customer fills in the last shop code they were given, so
       * whoever is typing can see where that customer got to and take it from
       * there. A customer with no orders yet fills in just their three
       * letters (HIY), and the number is typed in.
       */
      {
        name: "customer_id",
        label: "Customer",
        type: "select",
        required: true,
        optionsFrom: {
          url: `${API}/v2/order-options`,
          path: "customers",
          value: "id",
          label: "label",
        },
        fills: { target: "shop_code", compute: lastShopCode },
      },
      { name: "shop_code", label: "Shop Code", required: true },
      { name: "order_date", label: "Order Date", type: "date" },
      { name: "goods_type", label: "Type of goods" },
      { name: "first_amount", label: "F Amount", type: "number" },
      { name: "final_amount", label: "Final Amount", type: "number" },
      { name: "total_cbm", label: "T.CBM", type: "number" },
      { name: "total_kg", label: "T. KG", type: "number" },
      { name: "delivery_date", label: "D.Date", type: "date" },
      /**
       * Who settles with the supplier. Only a we_pay order raises the chase
       * and measurement notices, so an order created without this stays
       * silent - which is why the field is on the form rather than assumed.
       *
       * A we_pay order is forced to NEW on save whatever STATUS says: both
       * notices count from there, and an order that began life READY would
       * skip the workflow entirely.
       */
      {
        name: "pay_type",
        label: "Payment type",
        // two tabs rather than a dropdown: there are only ever two answers,
        // and which one an order is deserves to be readable without opening
        // anything. The colours are the ones the orders table tints with.
        type: "choice",
        // and at the head of the form rather than in the run of fields: it
        // decides what the order IS, and a we_pay one is put on a workflow
        // the other never sees
        atTop: true,
        options: PAY_TYPES,
      },
      /**
       * Where the order has got to. Every order starts at NEW and moves on
       * from the orders list - the STATUS column there, or the tick boxes
       * and "Set status" above it - so the create form does not offer it at
       * all: an order born READY skips the whole workflow, and the chase and
       * measurement notices count from NEW.
       *
       * `editOnly` keeps the box off the create form and `createValue` fills
       * NEW in behind it, found by name because the ids belong to the office
       * (they are rows in the status table, renameable from the Status
       * screen). If nobody has a status called NEW, nothing is sent and the
       * server's own default stands.
       */
      {
        name: "status_id",
        label: "STATUS",
        type: "select",
        editOnly: true,
        createValue: (list) => list.find((s) => /^\s*new\s*$/i.test(String(s.name || "")))?.id,
        optionsFrom: {
          url: `${API}/v2/order-options`,
          path: "statuses",
          value: "id",
          label: "name",
        },
      },
      /**
       * Which container this order travels in - a real container, picked by
       * file number. It is not the order's state; STATUS above is. An order
       * whose container is not decided yet is left empty.
       */
      {
        name: "con_no",
        label: "CON NO (container)",
        type: "select",
        optionsFrom: {
          url: `${API}/v2/order-options`,
          path: "con_no",
          value: "value",
          label: "label",
        },
      },
      { name: "cbm_price_note", label: "CBM price" },
      { name: "declaration", label: "Declaration" },
      { name: "packing_list", label: "packing list" },
      { name: "notes", label: "NOTE", multiline: true },
      /**
       * Separate from NOTE on purpose. NOTE is anything worth writing down;
       * this one raises a reminder on the day the container is expected to
       * load, so burying it in the general note would mean it never fires.
       */
      {
        name: "delivery_notes",
        label: "Notes on delivery (raises a reminder on loading day)",
        multiline: true,
      },
    ],
  },

  supplier: {
    title: "Add Supplier",
    endpoint: `${API}/v2/suppliers`,
    wide: true,
    fields: [
      { name: "code", label: "Code (e.g. SUP01)", required: true },
      { name: "name", label: "Supplier name", required: true },
      { name: "name_cn", label: "Chinese name" },
      { name: "contact_person", label: "Contact person" },
      { name: "phone", label: "Phone" },
      { name: "wechat", label: "WeChat" },
      { name: "email", label: "Email" },
      { name: "city", label: "City (Yiwu, Guangzhou ...)" },
      { name: "address", label: "Address" },
      { name: "currency", label: "Currency (CNY / USD)" },
      { name: "notes", label: "Notes", multiline: true },
    ],
  },

  /**
   * A purchase from one supplier for one customer. The order is optional on
   * purpose - goods are usually bought before it is decided which shipment
   * they travel in.
   */
  purchase: {
    title: "Add Purchase",
    endpoint: `${API}/v2/purchases`,
    wide: true,
    fields: [
      {
        name: "supplier_id",
        label: "Supplier",
        type: "select",
        required: true,
        // this endpoint returns a bare array, so no `path`
        optionsFrom: { url: `${API}/v2/suppliers`, value: "id", label: "name" },
      },
      {
        name: "customer_id",
        label: "Customer",
        type: "select",
        required: true,
        optionsFrom: {
          url: `${API}/v2/order-options`,
          path: "customers",
          value: "id",
          label: "label",
        },
      },
      { name: "invoice_no", label: "Supplier invoice no." },
      { name: "description", label: "Goods" },
      { name: "amount", label: "Amount", type: "number" },
      { name: "currency", label: "Currency (CNY)" },
      { name: "rate", label: "Rate to base (1 = same)", type: "number" },
      { name: "tracking_no", label: "Tracking no." },
      { name: "ctn", label: "CTN", type: "number" },
      { name: "cbm", label: "CBM", type: "number" },
      { name: "weight_kg", label: "Weight (kg)", type: "number" },
      { name: "notes", label: "Notes", multiline: true },
    ],
  },

  /**
   * Money paid out to a supplier. Three fields and nothing else - the
   * supplier, how much, and when.
   *
   * Anything extra a caller already knows (a purchase to tie it to, a
   * currency, a rate) is passed in through AddDialog's `defaults` and is
   * submitted with the rest, so the Customer Suppliers report can still open
   * this pointed at one specific invoice without the plain form growing
   * fields nobody fills in.
   */
  supplier_payment: {
    title: "Record Supplier Payment",
    endpoint: `${API}/v2/supplier-payments`,
    /**
     * Sent with every payment but never shown. The API reads `rate` directly
     * and errors with `Undefined array key "rate"` when a form leaves it out,
     * so it is always supplied. 1 means "the amount is already in the
     * accounting currency", which is what a three-field form implies.
     */
    defaults: { rate: 1, currency: "CNY" },
    fields: [
      {
        name: "supplier_id",
        label: "Supplier",
        type: "select",
        required: true,
        // `name` rather than a composed label: it is the only display field
        // the endpoint returns, and auto-created suppliers are named after
        // their shop code anyway
        optionsFrom: { url: `${API}/v2/suppliers`, value: "id", label: "name" },
      },
      { name: "amount", label: "Amount", type: "number", required: true },
      { name: "paid_at", label: "Payment date", type: "date", required: true },
    ],
  },

  post: {
    title: "Add Post",
    endpoint: `${API}/add_post`,
    // this one goes up as multipart because of the images
    multipart: true,
    edit: {
      // no `show`: /get_specefic_post/{id} throws on the server, so the row
      // from the list is passed in instead (AddDialog's `record` prop)
      submit: (id) => `${API}/edit_post/${id}`,
      method: "post",
    },
    fields: [
      { name: "body", label: "Body", required: true, multiline: true },
      { name: "image", label: "Images", type: "file", multiple: true },
    ],
  },

  message: {
    title: "Add Message",
    endpoint: `${API}/add_message`,
    edit: {
      show: (id) => `${API}/specefic_message/${id}`,
      unwrap: "message",
      submit: `${API}/edit_message`,
      method: "post",
      sendId: true,
    },
    fields: [
      { name: "body", label: "Message", required: true, multiline: true },
      {
        name: "customer_id",
        label: "Customer",
        type: "select",
        required: true,
        optionsFrom: { url: `${API}/customers`, path: "customers", value: "id", label: "name" },
      },
    ],
  },
};

export default entities;
